import Pinyin from 'pinyin';
import moment from 'moment';
import Decimal from 'decimal.js';
import { Modal } from 'view-design';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

// 获取系统指纹
export async function getSystemOid() {
    const fpPromise = FingerprintJS.load();
    const fp = await fpPromise;
    const result = await fp.get();
    return result.visitorId;
}
// 获取类型
export function getType(obj) {
    return Object.prototype.toString.call(obj);
}

// 判断是否是一个对象
export function isObject(obj) {
    return getType(obj) === '[object Object]';
}

// 判断是否是一个数组
export function isArray(obj) {
    return getType(obj) === '[object Array]';
}

// 判断是否非空, 防止隐式转换
export function isEmpty(obj) {
    let params = obj;

    const emptyTypeList = [undefined, null, ''];

    if (typeof params === 'string') {
        params = params.trim();
    }
    return emptyTypeList.includes(params);
}

// 判断不为空
export function isNotEmpty(obj) {
    return !isEmpty(obj);
}

// 深度拷贝 - 不包含函数拷贝
export function deepClone(obj, defaultData) {
    if (!isObject(obj) && !isArray(obj)) {
        if (defaultData) return defaultData;
        else return new Error('传入的参数必须是一个对象或一个数组');
    }
    return JSON.parse(JSON.stringify(obj));
}

/**
 * 深度拷贝 - 包含函数拷贝
 * @param {Object | Array<Object>} o
 * @return {Object | Array<Object}
 */
export function deepCopy(o) {
    let m;

    switch (true) {
        case isObject(o):
            m = {};
            let key, val;
            for ([key, val] of Object.entries(o)) {
                m[key] = deepCopy(val);
            }
            break;
        case isArray(o):
            m = [];
            o.forEach((tmp) => {
                m.push(deepCopy(tmp));
            });
            break;
        default:
            m = o;
    }
    return m;
}

/**
 * 获取localStorage的值，可选择是否序列化
 * @param {string} key 取值字段
 * @param {boolean} [useFormatter] 是否进行序列化
 * @param {any} [defaultData] 取值失败时使用的默认值
 */
export function getLocal(key, useFormatter, defaultData) {
    if (useFormatter) {
        let obj = window.localStorage.getItem(key);

        if (!obj) {
            return defaultData;
        } else {
            try {
                obj = JSON.parse(obj);
            } catch (e) {
                obj = defaultData;
            }
            return obj;
        }
    }
    return window.localStorage.getItem(key);
}

/**
 * 存储至localStorage
 * @param {string} key 存储字段
 * @param {Object | Array | string | number} obj 存储数据
 */
export function setLocal(key, obj) {
    const saveData = isObject(obj) || isArray(obj) ? JSON.stringify(obj) : obj;

    return window.localStorage.setItem(key, saveData);
}

/**
 * 防抖函数
 * @param {function} func 函数
 * @param {number} wait 时间(毫秒)
 * @return {function}
 */
export function debounce(func, wait) {
    let timer;

    return function () {
        const context = this,
            args = arguments;

        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(context, args);
        }, wait);
    };
}

/**
 * 数组根据某字段进行分组
 * @param {Object[]} list 需要进行分组的数组
 * @param {function(item: Object): string | Array | Symbol} fn 根据此回调返回的内容进行分组
 * @return {Object<Array>}
 */
export function groupBy(list, fn) {
    if (!Array.isArray(list)) return {};
    const obj = {};

    list.forEach((item) => {
        obj[fn(item)] = obj[fn(item)] || [];
        obj[fn(item)].push(item);
    });
    return obj;
}

/**
 * 绑定外部js的上下文, 请注意需要绑定this的函数必须声明为function函数而不是箭头函数, 否则函数的this将自动绑定
 * @param {Object[]} paramsList - 需要绑定的list
 * @param {string[]} keyList - key的集合
 * @param {Object} context - 传this
 */
export function bindContext(paramsList, keyList, context) {
    let list = [...paramsList];

    if (!list || !keyList || !context) return;
    list = deepCopy(list);
    list.forEach((item) => {
        keyList.forEach((key) => {
            if (item[key] instanceof Function) {
                item[key] = item[key].bind(context);
            }

            if (isObject(item[key])) {
                const obj = {};

                Object.keys(item[key]).forEach((itemKey) => {
                    if (item[key][itemKey] instanceof Function) {
                        obj[itemKey] = item[key][itemKey].bind(context);
                    } else {
                        obj[itemKey] = item[key][itemKey];
                    }
                });
                item[key] = obj;
            }
            if (item[key] instanceof Function) {
                item[key] = item[key].bind(context);
            }
        });
    });
    return list;
}

/**
 * 根据汉字获取对应的助计码
 * @param {String} value - 字符串
 */
export function getPinyinCode(str) {
    let value = str;

    if (typeof value !== 'string') return value;
    // 获取字符串中所有的汉字
    const matchArray = value.match(/[\u4E00-\u9FA5]/g);

    if (matchArray) {
        matchArray.forEach((item) => {
            // 将汉字转换成拼音，获取首字母
            const py = Pinyin(item, { style: Pinyin.STYLE_FIRST_LETTER }),
                firstChart = py.map((item) => item[0].substr(0, 1)).join('');

            // 将汉字替换成首字母
            value = value.replace(item, firstChart);
        });
    }
    return value.toUpperCase();
}

/**
 * 开始/结束日期互斥
 * @param {Date} currentDate - 当前日期
 * @param {string} type - 类型(start代表开始日期，end代表结束日期)
 * @param {Date} otherDate - 用于比较的日期
 * @return {boolean} - true代表禁用, false代表启用
 */
export function timeDisabled(currentDate, otherDate, type) {
    if (type === 'start' && currentDate && otherDate)
        return currentDate.getTime() > moment(otherDate).valueOf();
    if (type === 'end' && currentDate && otherDate)
        return currentDate.getTime() < moment(otherDate).valueOf();
    return false;
}

/**
 * 将数字转换为千分位逗号分隔字符串
 * @param {number} number
 */
export function toThousands(number) {
    if (typeof number !== 'number' || isNaN(number)) return '0';
    const prefix = number >= 0 ? '' : '-',
        afterNumber = String(number).split('.')[1] || '',
        mainNumber = String(Math.abs(parseInt(number))),
        cutLength = 3,
        mainNumberArr = mainNumber.split(''),
        startLength = mainNumberArr.length % cutLength,
        startStr = mainNumberArr.slice(0, startLength).join('');

    let resStr = '';

    for (let i = startLength; i < mainNumberArr.length; i += cutLength) {
        resStr += mainNumberArr.slice(i, i + cutLength).join('');
        if (i + cutLength < mainNumberArr.length) {
            resStr += ',';
        }
    }
    return `${prefix}${startStr || ''}${
        startStr && resStr ? ',' : ''
    }${resStr}${afterNumber ? '.' : ''}${afterNumber}`;
}

// decimal - 加法
export function addDecimal() {
    const array = Array.from(arguments),
        num = array.reduce((total, item) => {
            const _total = total || 0;
            const _item = item || 0;
            if (typeof _item !== 'string' && typeof _item !== 'number') {
                return _total;
            } else {
                return new Decimal(_total).add(new Decimal(_item));
            }
        });

    return num.toNumber();
}

// decimal - 减法
export function subDecimal() {
    const array = Array.from(arguments),
        num = array.reduce((total, item) => {
            const _total = total || 0;
            const _item = item || 0;
            if (typeof _item !== 'string' && typeof _item !== 'number') {
                return total;
            } else {
                return new Decimal(_total).sub(new Decimal(_item));
            }
        });

    return num.toNumber();
}

// decimal - 乘法
export function mulDecimal() {
    const array = Array.from(arguments),
        num = array.reduce((total, item) => {
            const _total = total || 0;
            const _item = item || 0;
            if (typeof _item !== 'string' && typeof _item !== 'number') {
                return _total;
            } else {
                return new Decimal(_total).mul(new Decimal(_item));
            }
        });

    return num.toNumber();
}

// decimal - 除法
export function divDecimal() {
    const array = Array.from(arguments),
        num = array.reduce((total, item) => {
            const _total = total || 0;
            const _item = item || 0;
            if (typeof _item !== 'string' && typeof _item !== 'number') {
                return _total;
            } else if (_item === 0 || _item === '0') {
                return new Decimal(0);
            } else {
                return new Decimal(_total).div(new Decimal(_item));
            }
        });

    return num.toNumber();
}

/**
 * decimal - 保留小数位
 * @param {Number|String} value - 要保留的值
 * @param {Number} digit - 保留的位数
 * @return {Number} - 返回数字
 */
export function fixedDecimal(value = 0, digit = 0) {
    const fixed = new Decimal(value).toFixed(digit);

    return new Decimal(fixed).toNumber();
}

/**
 * decimal - 保留小数位，不舍弃小数点后的0
 * @param {Number|String} paramsValue - 要保留的值
 * @param {Number} digit - 保留的位数
 * @param {Boolean} useThousands - 是否需要千分位分隔
 * @param {Boolean} skipEmpty - 不对空值进行转换
 * @return {String} - 返回字符串
 */
export function fixedDecimalString(
    paramsValue,
    digit = 0,
    useThousands = true,
    skipEmpty = false
) {
    let value = paramsValue;

    if (isEmpty(value) || isNaN(value)) {
        if (skipEmpty) {
            return '';
        } else {
            value = 0;
        }
    }
    // 如果需要千分位的分隔
    if (useThousands) {
        const fixed = new Decimal(value).toFixed(digit),
            arr = fixed.split('.');

        arr[0] = toThousands(parseInt(arr[0]));
        if (value < 0 && !arr[0].includes('-')) {
            arr[0] = '-' + arr[0];
        }
        return arr.join('.');
    } else {
        return new Decimal(value).toFixed(digit);
    }
}

/**
 * 根据form配置项创建一个默认表单对象
 * @param {Array<Object>} options - 表单配置项
 * @param {Object} defaultValue - 表单默认值
 */
export function createDefaultForm(options = [], defaultValue = {}) {
    const obj = {};

    if (!(options instanceof Array)) return obj;
    const keys = options.map((item) => item.key);

    keys.forEach((key, index) => {
        const value = defaultValue[key];

        if (isNotEmpty(value)) {
            obj[key] = value;
        } else {
            const defaultValue = options[index].defaultValue;

            obj[key] = isNotEmpty(defaultValue) ? defaultValue : '';
        }
    });
    return obj;
}

/**
 * 转换0和1为boolean, 请在提交时使用
 * @param {Object} form
 * @param {string[]} keyList
 * @return {Object}
 */
export function transStringToBoolean(form, keyList) {
    const obj = deepCopy(form);

    Object.keys(obj).forEach((key) => {
        if (keyList.includes(key)) {
            if (obj[key] !== '-1') {
                obj[key] = obj[key] === '1';
            } else {
                obj[key] = '';
            }
        }
    });
    return obj;
}

/**
 * 将boolean值转换为0和1，请在获取数据时使用
 * @param {Object} form
 * @param {string[]} keyList
 * @return {Object}
 */
export function transBooleanToString(form, keyList) {
    const obj = deepCopy(form);

    Object.keys(obj).forEach((key) => {
        if (keyList.includes(key)) {
            obj[key] = obj[key] === true ? 1 : 0;
        }
    });
    return obj;
}

/**
 * 将-1转换成空，用于转换查询下拉框的全部选项
 * @param {Object} form
 * @param {string[]} keyList
 * @return {Object}
 */
export function transMinusoneToEmpty(form, keyList) {
    const obj = deepCopy(form);

    Object.keys(obj).forEach((key) => {
        if (keyList.includes(key) && obj[key] === '-1') {
            obj[key] = '';
        }
    });
    return obj;
}

/**
 * 表格中的表单数据校验
 * @param {Object[]} tableData 表格数据
 * @param {Object} rules 校验规则
 * @param {String} title 校验标题-页面存在多个表格校验时可能会用到
 * @return {Promise}
 */
export function validateTableData(tableData, rules, title) {
    const errList = [];

    let state = true;

    tableData.forEach((item, index) => {
        let str = '',
            isFirst = false;

        Object.keys(rules).forEach((key) => {
            rules[key](item[key], item, (err) => {
                if (err) {
                    if (!isFirst) {
                        str += `第${index + 1}行：`;
                        isFirst = true;
                    }
                    state = false;
                    str += `${err.message};`;
                }
            });
        });
        errList.push(str);
    });

    if (!state) {
        Modal.error({
            title: title || '校验失败',
            render: (h) => (
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    {errList.map((item) => {
                        const str = item.split(';').join(', ');

                        return <p>{str.substring(0, str.length - 2)}</p>;
                    })}
                </div>
            ),
            duration: 10,
            closable: true,
        });
        return Promise.reject(new Error('校验失败'));
    }
    return Promise.resolve();
}

/**
 * 双数组对比取出不同部分
 * @param {Object[]} list1 原始数组
 * @param {Object[]} list2 新数组
 * @param {string} onlyKey 主键key
 * @return {Object[]} 不同项数组
 */
export function excludeArray(list1, list2, onlyKey) {
    if (!isArray(list1) || !isArray(list2)) {
        // eslint-disable-next-line no-console
        console.error('传入的前两个参数必须都为数组');
        return [];
    }
    if (isEmpty(onlyKey)) {
        // eslint-disable-next-line no-console
        console.error('请传入对比用的key, 且必须为唯一值字段');
        return [];
    }
    const arr = [];

    list2.forEach((item) => {
        const findData = list1.find((tmp) => tmp[onlyKey] === item[onlyKey]);

        if (!findData) arr.push(item);
    });
    return arr;
}

/**
 * 字符串转字节数组
 * @param {string} str
 * @return {any[]}
 */
export function stringToByteArray(str) {
    const bytes = [];
    let c;
    const len = str.length;
    for (let i = 0; i < len; i++) {
        c = str.charCodeAt(i);
        if (c >= 0x010000 && c <= 0x10ffff) {
            bytes.push(((c >> 18) & 0x07) | 0xf0);
            bytes.push(((c >> 12) & 0x3f) | 0x80);
            bytes.push(((c >> 6) & 0x3f) | 0x80);
            bytes.push((c & 0x3f) | 0x80);
        } else if (c >= 0x000800 && c <= 0x00ffff) {
            bytes.push(((c >> 12) & 0x0f) | 0xe0);
            bytes.push(((c >> 6) & 0x3f) | 0x80);
            bytes.push((c & 0x3f) | 0x80);
        } else if (c >= 0x000080 && c <= 0x0007ff) {
            bytes.push(((c >> 6) & 0x1f) | 0xc0);
            bytes.push((c & 0x3f) | 0x80);
        } else {
            bytes.push(c & 0xff);
        }
    }
    return bytes;
}

/**
 * 密码转hex
 * @param {any[]} byteArray
 * @return {string}
 */
export function byteToHex(byteArray) {
    const HEX_ARRAY_STR = '0123456789ABCDEF';
    const HEX_ARRAY = HEX_ARRAY_STR.split('');
    const arr = new Array(byteArray.length * 2);
    for (let j = 0; j < byteArray.length; ++j) {
        const v = byteArray[j] & 255;
        arr[j * 2] = HEX_ARRAY[v >>> 4];
        arr[j * 2 + 1] = HEX_ARRAY[v & 15];
    }
    return arr.join('');
}
/**
 * 双数组批量对比删除方法
 * @param {Object[]} list1 - 被选中数组
 * @param {Object[]} list2 - 原始对照数组
 * @param {string} key - 对比的key
 * @return {Array}
 */
export function dbListSplice(list1, list2, key) {
    const copyList = JSON.parse(JSON.stringify(list2));
    for (let i = 0; i < list1.length; i++) {
        for (let j = 0; j < copyList.length; j++) {
            if (list1[i][key] === copyList[j][key]) {
                copyList.splice(j, 1);
                j--;
            }
        }
    }
    return copyList;
}
/**
 * 数组转树
 * @param {Array} arr 数组
 * @param {String} idName 唯一的属性名
 * @param {String} parentIdName 父级唯一的属性名
 * @param {Function | Null} [callback] 新数据的转换函数
 * @return {Object[]}
 */
export function arrToTree(arr, idName, parentIdName, callback = null) {
    const dic = arr.reduce((p, c) => {
        const temp = callback ? callback(c, p) : c;
        temp.children = temp.children || [];
        p[c[idName]] = temp;
        return p;
    }, {});
    const root = [];
    for (const [, item] of Object.entries(dic)) {
        if (item[parentIdName] && dic[item[parentIdName]]) {
            dic[item[parentIdName]].children.push(item);
        } else {
            root.push(item);
        }
    }
    return root;
}

/**
 * 数字转中文大写字符
 * @param {string | number} money
 * @return {string}
 */
/* eslint-disable */
export function intToChinese(money) {
    var cnNums = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']; //汉字的数字
    var cnIntRadice = ['', '拾', '佰', '仟']; //基本单位
    var cnIntUnits = ['', '万', '亿', '兆']; //对应整数部分扩展单位
    var cnDecUnits = ['角', '分', '毫', '厘']; //对应小数部分单位
    var cnInteger = '整'; //整数金额时后面跟的字符
    var cnIntLast = '元'; //整型完以后的单位
    var maxNum = 999999999999999.9999; //最大处理的数字
    var IntegerNum; //金额整数部分
    var DecimalNum; //金额小数部分
    var ChineseStr = ''; //输出的中文金额字符串
    var parts; //分离金额后用的数组，预定义
    var Symbol = ''; //正负值标记
    if (money == '') {
        return '';
    }

    money = parseFloat(money);
    if (money >= maxNum) {
        alert('超出最大处理数字');
        return '';
    }
    if (money == 0) {
        ChineseStr = cnNums[0] + cnIntLast + cnInteger;
        return ChineseStr;
    }
    if (money < 0) {
        money = -money;
        Symbol = '负 ';
    }
    money = money.toString(); //转换为字符串
    if (money.indexOf('.') == -1) {
        IntegerNum = money;
        DecimalNum = '';
    } else {
        parts = money.split('.');
        IntegerNum = parts[0];
        DecimalNum = parts[1].substr(0, 4);
    }
    if (parseInt(IntegerNum, 10) > 0) {
        //获取整型部分转换
        var zeroCount = 0;
        var IntLen = IntegerNum.length;
        for (var i = 0; i < IntLen; i++) {
            var n = IntegerNum.substr(i, 1);
            var p = IntLen - i - 1;
            var q = p / 4;
            var m = p % 4;
            if (n == '0') {
                zeroCount++;
            } else {
                if (zeroCount > 0) {
                    ChineseStr += cnNums[0];
                }
                zeroCount = 0; //归零
                ChineseStr += cnNums[parseInt(n)] + cnIntRadice[m];
            }
            if (m == 0 && zeroCount < 4) {
                ChineseStr += cnIntUnits[q];
            }
        }
        ChineseStr += cnIntLast;
        //整型部分处理完毕
    }
    if (DecimalNum != '') {
        //小数部分
        var decLen = DecimalNum.length;
        for (var i = 0; i < decLen; i++) {
            var n = DecimalNum.substr(i, 1);
            if (n != '0') {
                ChineseStr += cnNums[Number(n)] + cnDecUnits[i];
            }
        }
    }
    if (ChineseStr == '') {
        ChineseStr += cnNums[0] + cnIntLast + cnInteger;
    } else if (DecimalNum == '') {
        ChineseStr += cnInteger;
    }
    ChineseStr = Symbol + ChineseStr;

    return ChineseStr;
}
/* eslint-disable */
function numberToChinese(str, isDecimal) {
    str = str + '';
    var len = str.length - 1;
    var idxs = [
        '',
        '十',
        '百',
        '千',
        '万',
        '十',
        '百',
        '千',
        '亿',
        '十',
        '百',
        '千',
        '万',
        '十',
        '百',
        '千',
        '亿',
    ];
    var num = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    return str.replace(/([1-9]|0+)/g, function ($, $1, idx, full) {
        var pos = 0;
        if ($1[0] != '0') {
            pos = len - idx;
            if (idx == 0 && $1[0] == 1 && idxs[len - idx] == '十') {
                return isDecimal ? '' : idxs[len - idx];
            }
            return num[$1[0]] + (isDecimal ? '' : idxs[len - idx]);
        } else {
            var left = len - idx;
            var right = len - idx + $1.length;
            if (Math.floor(right / 4) - Math.floor(left / 4) > 0) {
                pos = left - (left % 4);
            }
            if (pos) {
                return (isDecimal ? '' : idxs[pos]) + num[$1[0]];
            } else if (idx + $1.length >= len) {
                return '';
            } else {
                return num[$1[0]];
            }
        }
    });
}

/**
 * 数字转中文大写（非金额）
 * @param {string | number} number
 * @return {string}
 */
export function numToChn(number) {
    const str = String(number);
    const arr = str.split('.');
    let result;
    if (arr.length === 1) {
        result = numberToChinese(str);
    }
    if (arr.length === 2) {
        result = `${numberToChinese(arr[0])}点${numberToChinese(arr[1], true)}`;
    }
    return result;
}

/**
 * 使用文件原名称下载文件
 * @param {string} path 文件下载路径
 * @param {string} sourceName 文件原名称
 * @param {string} sourceType 文件类型
 */
export function downloadFile(path, sourceName, sourceType) {
    const request = new XMLHttpRequest(),
        fileUrl = path,
        fileName = sourceName + sourceType;

    request.open('GET', fileUrl, true);
    request.responseType = 'blob';
    request.onload = function (e) {
        const tempUrl = window.URL.createObjectURL(request.response),
            href = document.createElement('a');
        href.href = tempUrl;
        href.download = fileName;
        href.click();
        href.remove();
    };
    request.send();
}

//get the IP addresses associated with an account
function getIPs(callback) {
    var ip_dups = {};

    //compatibility for firefox and chrome
    var RTCPeerConnection =
        window.RTCPeerConnection ||
        window.mozRTCPeerConnection ||
        window.webkitRTCPeerConnection;
    var useWebKit = !!window.webkitRTCPeerConnection;

    //bypass naive webrtc blocking using an iframe
    if (!RTCPeerConnection) {
        //NOTE: you need to have an iframe in the page right above the script tag
        //
        //<iframe id="iframe" sandbox="allow-same-origin" style="display: none"></iframe>
        //<script>...getIPs called in here...
        //
        var win = iframe.contentWindow;
        RTCPeerConnection =
            win.RTCPeerConnection ||
            win.mozRTCPeerConnection ||
            win.webkitRTCPeerConnection;
        useWebKit = !!win.webkitRTCPeerConnection;
    }

    //minimal requirements for data connection
    var mediaConstraints = {
        optional: [{ RtpDataChannels: true }],
    };

    var servers = { iceServers: [{ urls: 'stun:stun.services.mozilla.com' }] };

    //construct a new RTCPeerConnection
    var pc = new RTCPeerConnection(servers, mediaConstraints);

    function handleCandidate(candidate) {
        //match just the IP address
        // var ip_regex = /([0-9]{1,3}(\.[0-9]{1,3}){3}|[a-f0-9]{1,4}(:[a-f0-9]{1,4}){7})/
        var ip_addr = candidate;

        //remove duplicates
        if (ip_dups[ip_addr] === undefined) callback(ip_addr);

        ip_dups[ip_addr] = true;
    }

    //listen for candidate events
    pc.onicecandidate = function (ice) {
        //skip non-candidate events
        if (ice.candidate) handleCandidate(ice.candidate.candidate);
    };

    //create a bogus data channel
    pc.createDataChannel('');

    //create an offer sdp
    pc.createOffer(
        function (result) {
            //trigger the stun server request
            pc.setLocalDescription(
                result,
                function () {},
                function () {}
            );
        },
        function () {}
    );

    //wait for a while to let everything done
    // setTimeout(function(){
    //     //read candidate info from local description
    //     var lines = pc.localDescription.sdp.split('\n');
    //
    //     lines.forEach(function(line){
    //         if(line.indexOf('a=candidate:') === 0)
    //             handleCandidate(line);
    //     });
    // }, 1000);
}

function getIpPromise() {
    return new Promise((resolve) => {
        getIPs(resolve);
    });
}

// 获取密码安全等级
export const getPwdLevel = function (pwd) {
    let regArr = [/[a-z]/, /[A-Z]/, /\d/, /_|~|@|#|\$|\^/];

    regArr = regArr.filter((item) => item.test(pwd));
    return regArr.length;
};

/**
 * 转换对象中的-1为null
 * @param {Object} params
 * @return {*}
 */
export function formatAllTypeParams(params) {
    if (!isObject(params)) return params;
    const obj = { ...params };
    Object.keys(obj).forEach((key) => {
        if (obj[key] === '-1') {
            obj[key] = null;
        }
    });
    return obj;
}

/**
 * 获取拍照图片
 * @param {File} file
 * @param {function(formData: FormData)} callback
 */
export function getImage(file, callback, type) {
    const reader = new FileReader();
    // 文件转换为base64字符串
    reader.readAsDataURL(file);
    reader.addEventListener('load', function (event) {
        const image = new Image();
        image.src = event.target.result;
        image.addEventListener('load', function () {
            // 图片加载完成后进行图片重绘和质量压缩，之后转换为FormData格式
            compressImage(
                image,
                (dataURL) => {
                    callback && callback(getImageFormData(dataURL, file, type));
                },
                file
            );
        });
    });
}

/**
 * 压缩图片
 * @param {HTMLImageElement} image img元素对象
 * @param {function(dataURL: string)} callback 回调
 * @param {File} file
 */
function compressImage(image, callback, file) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const width = image.width;
    const height = image.height;
    const limit = 1048576; // 1024*1024 kb -> 1 MB
    const quality =
        file.size > limit
            ? numberUtils.toFixed(numberUtils.div(limit, file.size), 2)
            : 1;
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(image, 0, 0, width, height);
    callback && callback(canvas.toDataURL('image/jpeg', quality));
}

/**
 * 临时路径转blob对象
 * @param {string} dataURL 文件base64
 * @return {Blob} blob对象(文件类型分为File和Blob)
 */
function dataURItoBlob(dataURL) {
    // dataURL是base64格式
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const byteString = atob(arr[1]);
    let len = byteString.length;
    const u8arr = new Uint8Array(len);
    while (len--) {
        u8arr[len] = byteString.charCodeAt(len);
    }
    return new Blob([u8arr], { type: mime });
}

/**
 * 构建FormData类型载体
 * @param {string} dataURL
 * @param {File} file
 * @param {string} type OCR图像识别类型枚举 - 04: 车牌, 07: 营业执照
 * @return {FormData}
 */
function getImageFormData(dataURL, file, type) {
    const blob = dataURItoBlob(dataURL);
    const formData = new FormData();
    formData.append('file', blob, file.name);
    formData.append('type', type);
    return formData;
}
